import React, { useEffect, useState } from 'react';

const FoodSuccesful = () => {
  const [billingDetails, setBillingDetails] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);

  useEffect(() => {
    // Retrieve data from localStorage
    const billingData = localStorage.getItem('billingDetails');
    const bookingData = localStorage.getItem('bookingDetails');

    if (billingData) {
      setBillingDetails(JSON.parse(billingData));
    }

    if (bookingData) {
      setBookingDetails(JSON.parse(bookingData));
    }

    setLoading(false);
  }, []);

  const handleDone = () => {
    window.location.href = '/reservation/options/booking/welcome';
  };

  // Function to format date from YYYY-MM-DD to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';

    try {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-200 h-12 w-12"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!billingDetails || !bookingDetails) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white">
        <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-xl">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Order Details Not Found</h2>
          <p className="text-gray-600 mb-6">
            We couldn't retrieve your order information. Please contact our support team.
          </p>
          <button
            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-md"
            onClick={() => (window.location.href = '/')}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Extract booking details from the nested structure
  const primaryBooking = bookingDetails.primaryBooking || bookingDetails.bookings?.[0];
  const roomType = primaryBooking?.room_type?.name || 'N/A';
  const roomNumber = primaryBooking?.room?.room_number || 'N/A';
  const checkinDate = formatDate(primaryBooking?.checkin_date) || 'N/A';
  const checkoutDate = formatDate(primaryBooking?.checkout_date) || 'N/A';
  const numberOfGuests = primaryBooking?.number_of_guests || 'N/A';
  const fullName = primaryBooking?.guest_name || 'N/A';
  const contactNumber = primaryBooking?.guest_phone || 'N/A';
  const email = primaryBooking?.guest_email || 'N/A';
  const bookingType = primaryBooking?.booking_status || 'N/A';

  // Get order ID from booking details (using order_id from the root level or primaryBooking)
  const orderId = bookingDetails.order_id || primaryBooking?.order_id || 'N/A';

  // Calculate CGST and SGST (50% each of total tax)
  const cgstAmount = (billingDetails.taxamount / 2).toFixed(2);
  const sgstAmount = (billingDetails.taxamount / 2).toFixed(2);

  return (
    <div className=" min-h-screen bg-white">
      <div className=" mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Modern Card Container */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-black to-gray-600 p-8 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center mb-6 md:mb-0">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Order Confirmed!</h1>
                  <p className="text-indigo-100">
                    Thank you for your order, {fullName.split(' ')[0]}!
                  </p>
                </div>
              </div>
              <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
                <p className="font-medium">Reservation number: {orderId}</p>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
            {/* Customer Details */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Customer Details</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p className="font-medium text-transform: capitalize">{fullName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Contact Number</p>
                    <p className="font-medium">{contactNumber}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Check-in Date</p>
                    <p className="font-medium">{checkinDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Check-out Date</p>
                    <p className="font-medium">{checkoutDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Number of Guests</p>
                    <p className="font-medium">{numberOfGuests}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 p-6 rounded-xl h-full">
                <h2 className="text-xl font-semibold text-gray-800 mb-6">Order Summary</h2>

                {/* Order Timeline */}
                <div className="mb-8">
                  <div className="relative">
                    <div className="absolute h-full w-0.5 bg-gray-600 left-4 top-6"></div>

                    <div className="relative flex items-start mb-8">
                      <div className="bg-black rounded-full h-8 w-8 flex items-center justify-center z-10">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <div className="ml-6">
                        <h3 className="font-medium text-gray-800">Order Received</h3>
                        <p className="text-sm text-gray-500 mt-1">We've received your food order</p>
                      </div>
                    </div>

                    <div className="relative flex items-start mb-8">
                      <div className="bg-gray-500 border-2 border-black rounded-full h-8 w-8 flex items-center justify-center z-10">
                        <div className="bg-white rounded-full h-2 w-2"></div>
                      </div>
                      <div className="ml-6">
                        <h3 className="font-medium text-gray-800">Preparing Your Food</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Our chefs are working on your order
                        </p>
                      </div>
                    </div>

                    <div className="relative flex items-start">
                      <div className="bg-white border-2 border-gray-300 rounded-full h-8 w-8 flex items-center justify-center z-10">
                        <div className="bg-gray-400 rounded-full h-2 w-2"></div>
                      </div>
                      <div className="ml-6">
                        <h3 className="font-medium text-gray-800">Delivery to Room</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Estimated delivery time: 30-45 minutes
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-4">Payment Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">
                        ₹
                        {Number(billingDetails.baseamount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <span className="text-gray-600">Tax </span>
                        <div
                          className="relative ml-2"
                          onMouseEnter={() => setShowTaxBreakdown(true)}
                          onMouseLeave={() => setShowTaxBreakdown(false)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-gray-500 cursor-pointer"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {showTaxBreakdown && (
                            <div className="absolute z-10 left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1">
                              <div className="px-4 py-2 text-sm text-gray-700 border-b">
                                <div className="flex justify-between">
                                  <span>CGST (6%):</span>
                                  <span>
                                    ₹
                                    {Number(cgstAmount).toLocaleString('en-IN', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                              </div>
                              <div className="px-4 py-2 text-sm text-gray-700">
                                <div className="flex justify-between">
                                  <span>SGST (6%):</span>
                                  <span>
                                    ₹
                                    {Number(sgstAmount).toLocaleString('en-IN', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <span className="font-medium">
                        ₹
                        {Number(billingDetails.taxamount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between pt-3 border-t border-gray-200">
                      <span className="text-lg font-semibold">Total</span>
                      <span className="text-lg font-bold text-gray-800">
                        ₹
                        {Number(billingDetails.finalamount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-end gap-4">
              <button
                className="px-14 py-3 bg-black text-white rounded-lg hover:text-gray-300 font-semibold transition-colors shadow-md flex items-center justify-center"
                onClick={handleDone}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodSuccesful;

// import React, { useEffect, useState } from "react";

// const FoodSuccesful = () => {
//   const [billingDetails, setBillingDetails] = useState(null);
//   const [bookingDetails, setBookingDetails] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);

//   useEffect(() => {
//     // Retrieve data from localStorage
//     const billingData = localStorage.getItem("billingDetails");
//     const bookingData = localStorage.getItem("bookingDetails");

//     if (billingData) {
//       setBillingDetails(JSON.parse(billingData));
//     }

//     if (bookingData) {
//       setBookingDetails(JSON.parse(bookingData));
//     }

//     setLoading(false);
//   }, []);

//   const handleDone = () => {

//     window.location.href = "/reservation/options/booking/welcome";
//   };

//   if (loading) {
//     return (
//       <div className="w-full h-screen flex items-center justify-center bg-gray-50">
//         <div className="animate-pulse flex space-x-4">
//           <div className="rounded-full bg-gray-200 h-12 w-12"></div>
//           <div className="flex-1 space-y-4 py-1">
//             <div className="h-4 bg-gray-200 rounded w-3/4"></div>
//             <div className="space-y-2">
//               <div className="h-4 bg-gray-200 rounded"></div>
//               <div className="h-4 bg-gray-200 rounded w-5/6"></div>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (!billingDetails || !bookingDetails) {
//     return (
//       <div className="w-full h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white">
//         <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-xl">
//           <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
//             <svg
//               xmlns="http://www.w3.org/2000/svg"
//               className="h-10 w-10 text-red-500"
//               fill="none"
//               viewBox="0 0 24 24"
//               stroke="currentColor"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M6 18L18 6M6 6l12 12"
//               />
//             </svg>
//           </div>
//           <h2 className="text-2xl font-bold text-gray-800 mb-4">
//             Order Details Not Found
//           </h2>
//           <p className="text-gray-600 mb-6">
//             We couldn't retrieve your order information. Please contact our
//             support team.
//           </p>
//           <button
//             className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-md"
//             onClick={() => (window.location.href = "/")}
//           >
//             Return to Home
//           </button>
//         </div>
//       </div>
//     );
//   }

//   // Extract booking details from the nested structure
//   const primaryBooking =
//     bookingDetails.primaryBooking || bookingDetails.bookings?.[0];
//   const roomType = primaryBooking?.room_type?.name || "N/A";
//   const roomNumber = primaryBooking?.room?.room_number || "N/A";
//   const checkinDate = primaryBooking?.checkin_date || "N/A";
//   const checkoutDate = primaryBooking?.checkout_date || "N/A";
//   const numberOfGuests = primaryBooking?.number_of_guests || "N/A";
//   const fullName = primaryBooking?.guest_name || "N/A";
//   const contactNumber = primaryBooking?.guest_phone || "N/A";
//   const email = primaryBooking?.guest_email || "N/A";
//   const bookingType = primaryBooking?.booking_status || "N/A";

//   // Calculate CGST and SGST (50% each of total tax)
//   const cgstAmount = (billingDetails.taxamount / 2).toFixed(2);
//   const sgstAmount = (billingDetails.taxamount / 2).toFixed(2);

//   return (
//     <div className=" min-h-screen bg-white">
//       <div className=" mx-auto px-4 sm:px-6 lg:px-8 py-10">
//         {/* Modern Card Container */}
//         <div className="bg-white rounded-xl shadow-xl overflow-hidden">
//           {/* Header Section with Gradient */}
//           <div className="bg-gradient-to-r from-black to-gray-600 p-8 text-white">
//             <div className="flex flex-col md:flex-row md:items-center md:justify-between">
//               <div className="flex items-center mb-6 md:mb-0">
//                 <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     className="h-8 w-8"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                     stroke="currentColor"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M5 13l4 4L19 7"
//                     />
//                   </svg>
//                 </div>
//                 <div>
//                   <h1 className="text-3xl font-bold">Order Confirmed!</h1>
//                   <p className="text-indigo-100">
//                     Thank you for your order, {fullName.split(" ")[0]}!
//                   </p>
//                 </div>
//               </div>
//               <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
//                 <p className="font-medium">
//                   Order #: {billingDetails.bookingid}
//                 </p>
//               </div>
//             </div>
//           </div>

//           {/* Content Grid */}
//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
//             {/* Customer Details */}
//             <div className="lg:col-span-1">
//               <div className="bg-gray-50 p-6 rounded-xl">
//                 <h2 className="text-xl font-semibold text-gray-800 mb-4">
//                   Customer Details
//                 </h2>
//                 <div className="space-y-4">
//                   <div>
//                     <p className="text-sm text-gray-500">Full Name</p>
//                     <p className="font-medium text-transform: capitalize">
//                       {fullName}
//                     </p>
//                   </div>
//                   <div>
//                     <p className="text-sm text-gray-500">Email</p>
//                     <p className="font-medium">{email}</p>
//                   </div>
//                   <div>
//                     <p className="text-sm text-gray-500">Check-in Date</p>
//                     <p className="font-medium">{checkinDate}</p>
//                   </div>
//                   <div>
//                     <p className="text-sm text-gray-500">Check-out Date</p>
//                     <p className="font-medium">{checkoutDate}</p>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Order Summary */}
//             <div className="lg:col-span-2">
//               <div className="bg-gray-50 p-6 rounded-xl h-full">
//                 <h2 className="text-xl font-semibold text-gray-800 mb-6">
//                   Order Summary
//                 </h2>

//                 {/* Order Timeline */}
//                 <div className="mb-8">
//                   <div className="relative">
//                     <div className="absolute h-full w-0.5 bg-gray-600 left-4 top-6"></div>

//                     <div className="relative flex items-start mb-8">
//                       <div className="bg-black rounded-full h-8 w-8 flex items-center justify-center z-10">
//                         <svg
//                           xmlns="http://www.w3.org/2000/svg"
//                           className="h-4 w-4 text-white"
//                           fill="none"
//                           viewBox="0 0 24 24"
//                           stroke="currentColor"
//                         >
//                           <path
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             strokeWidth={2}
//                             d="M5 13l4 4L19 7"
//                           />
//                         </svg>
//                       </div>
//                       <div className="ml-6">
//                         <h3 className="font-medium text-gray-800">
//                           Order Received
//                         </h3>
//                         <p className="text-sm text-gray-500 mt-1">
//                           We've received your food order
//                         </p>
//                       </div>
//                     </div>

//                     <div className="relative flex items-start mb-8">
//                       <div className="bg-gray-500 border-2 border-black rounded-full h-8 w-8 flex items-center justify-center z-10">
//                         <div className="bg-white rounded-full h-2 w-2"></div>
//                       </div>
//                       <div className="ml-6">
//                         <h3 className="font-medium text-gray-800">
//                           Preparing Your Food
//                         </h3>
//                         <p className="text-sm text-gray-500 mt-1">
//                           Our chefs are working on your order
//                         </p>
//                       </div>
//                     </div>

//                     <div className="relative flex items-start">
//                       <div className="bg-white border-2 border-gray-300 rounded-full h-8 w-8 flex items-center justify-center z-10">
//                         <div className="bg-gray-400 rounded-full h-2 w-2"></div>
//                       </div>
//                       <div className="ml-6">
//                         <h3 className="font-medium text-gray-800">
//                           Delivery to Room
//                         </h3>
//                         <p className="text-sm text-gray-500 mt-1">
//                           Estimated delivery time: 30-45 minutes
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Payment Summary */}
//                 <div className="border-t border-gray-200 pt-6">
//                   <h3 className="text-lg font-medium text-gray-800 mb-4">
//                     Payment Details
//                   </h3>
//                   <div className="space-y-3">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Subtotal</span>
//                       <span className="font-medium">
//                         ₹
//                         {Number(billingDetails.baseamount).toLocaleString(
//                           "en-IN",
//                           { minimumFractionDigits: 2, maximumFractionDigits: 2 }
//                         )}
//                       </span>
//                     </div>
//                     <div className="flex justify-between items-center">
//                       <div className="flex items-center">
//                         <span className="text-gray-600">Tax </span>
//                         <div
//                           className="relative ml-2"
//                           onMouseEnter={() => setShowTaxBreakdown(true)}
//                           onMouseLeave={() => setShowTaxBreakdown(false)}
//                         >
//                           <svg
//                             xmlns="http://www.w3.org/2000/svg"
//                             className="h-4 w-4 text-gray-500 cursor-pointer"
//                             fill="none"
//                             viewBox="0 0 24 24"
//                             stroke="currentColor"
//                           >
//                             <path
//                               strokeLinecap="round"
//                               strokeLinejoin="round"
//                               strokeWidth={2}
//                               d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
//                             />
//                           </svg>
//                           {showTaxBreakdown && (
//                             <div className="absolute z-10 left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1">
//                               <div className="px-4 py-2 text-sm text-gray-700 border-b">
//                                 <div className="flex justify-between">
//                                   <span>CGST (6%):</span>
//                                   <span>
//                                     ₹
//                                     {Number(cgstAmount).toLocaleString(
//                                       "en-IN",
//                                       {
//                                         minimumFractionDigits: 2,
//                                         maximumFractionDigits: 2,
//                                       }
//                                     )}
//                                   </span>
//                                 </div>
//                               </div>
//                               <div className="px-4 py-2 text-sm text-gray-700">
//                                 <div className="flex justify-between">
//                                   <span>SGST (6%):</span>
//                                   <span>
//                                     ₹
//                                     {Number(sgstAmount).toLocaleString(
//                                       "en-IN",
//                                       {
//                                         minimumFractionDigits: 2,
//                                         maximumFractionDigits: 2,
//                                       }
//                                     )}
//                                   </span>
//                                 </div>
//                               </div>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                       <span className="font-medium">
//                         ₹
//                         {Number(billingDetails.taxamount).toLocaleString(
//                           "en-IN",
//                           { minimumFractionDigits: 2, maximumFractionDigits: 2 }
//                         )}
//                       </span>
//                     </div>
//                     <div className="flex justify-between pt-3 border-t border-gray-200">
//                       <span className="text-lg font-semibold">Total</span>
//                       <span className="text-lg font-bold text-gray-800">
//                         ₹
//                         {Number(billingDetails.finalamount).toLocaleString(
//                           "en-IN",
//                           { minimumFractionDigits: 2, maximumFractionDigits: 2 }
//                         )}
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
//             <div className="flex flex-col sm:flex-row justify-end gap-4">
//               <button
//                 className="px-14 py-3 bg-black text-white rounded-lg hover:text-gray-300 font-semibold transition-colors shadow-md flex items-center justify-center"
//                 onClick={handleDone}
//               >
//                 Done
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default FoodSuccesful;
